<template>
  <div class="tenant-form">
    <div class="header-actions">
      <h2>Add New Tenant</h2>
      <button type="button" class="import-btn" @click="showImportModal = true">
        Import Tenants from Spreadsheet
      </button>
    </div>

    <!-- Import Modal -->
    <div v-if="showImportModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Import Tenants from Excel</h3>
          <button class="close-btn" @click="showImportModal = false">&times;</button>
        </div>
        
        <div class="modal-body">
          <div class="file-upload-section">
            <input 
              type="file" 
              id="excel-file" 
              accept=".xlsx, .xls" 
              @change="handleFileUpload" 
              ref="fileInput"
            />
            <label for="excel-file" class="file-label">
              {{ selectedFileName || 'Choose Excel File' }}
            </label>
          </div>

          <div v-if="previewData.length > 0" class="preview-section">
            <h4>Preview (First 5 records)</h4>
            <div class="table-container">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Display As</th>
                    <th>First Name</th>
                    <th>Surname</th>
                    <th>Email</th>
                    <th>Phone</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(tenant, index) in previewData" :key="index">
                    <td>{{ tenant.reference }}</td>
                    <td>{{ tenant.displayAs }}</td>
                    <td>{{ tenant.person?.firstName }}</td>
                    <td>{{ tenant.person?.surname }}</td>
                    <td>{{ tenant.email }}</td>
                    <td>{{ tenant.number }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="modal-actions">
            <button 
              type="button" 
              class="btn-secondary" 
              @click="showImportModal = false"
            >
              Cancel
            </button>
            <button 
              type="button" 
              class="btn-primary" 
              @click="importTenants" 
              :disabled="!selectedFile || importLoading"
            >
              <span v-if="importLoading">Importing...</span>
              <span v-else>Import Tenants</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Form -->
    <form @submit.prevent="submitTenant">
      <!-- Tenant Type Section -->
      <div class="section">
        <h3>Tenant Type</h3>
        <div class="form-group">
          <label for="type">Type:</label>
          <select id="type" v-model="tenant.type" required>
            <option value="Residential">Residential</option>
            <option value="Commercial">Commercial</option>
          </select>
           <br>
          <div class="form-group">
          <label for="display-as">Display As:</label>
          <input type="text" id="display-as" v-model="tenant.displayAs" required />
        </div>
       
        </div>
      </div>
      <!-- Residential Details -->
      <div v-if="tenant.type === 'Residential'" class="section">
        <h3>Residential Details</h3>
        <div class="form-group">
          <label for="first-name">First Name:</label>
          <input type="text" id="first-name" v-model="tenant.person.firstName" required />
        </div>
        <div class="form-group">
          <label for="surname">Surname:</label>
          <input type="text" id="surname" v-model="tenant.person.surname" required />
        </div>
        <div class="form-group">
          <label for="date-of-birth">Date of Birth:</label>
          <input type="date" id="date-of-birth" v-model="tenant.person.dateOfBirth" required />
        </div>
        <div class="form-group">
          <label for="identifier">Identifier (RSA ID/Passport):</label>
          <input type="text" id="identifier" v-model="tenant.identifier" required />
        </div>
      </div>
      <!-- Contact Information -->
      <div class="section">
        <h3>Contact Information</h3>
        <div class="form-group">
          <label for="email">Email:</label>
          <input type="email" id="email" v-model="tenant.email" required />
        </div>
        <div class="form-group">
          <label for="number">Phone Number:</label>
          <input type="tel" id="number" v-model="tenant.number" required />
        </div>
      </div>
      <!-- Commercial Details -->
      <div v-if="tenant.type === 'Commercial'" class="section">
        <h3>Commercial Details</h3>
        <div class="form-group">
          <label for="legal-name">Legal Name:</label>
          <input type="text" id="legal-name" v-model="tenant.legalName" required />
        </div>
        <div class="form-group">
          <label for="trading-as">Trading As:</label>
          <input type="text" id="trading-as" v-model="tenant.tradingName" required />
        </div>
        <div class="form-group">
          <label for="registration-number">Registration Number:</label>
          <input type="text" id="registration-number" v-model="tenant.registrationNumber" required />
        </div>
        <div class="form-group">
          <label for="trust-npo-gov-number">Trust/NPO/Gov Number:</label>
          <input type="text" id="trust-npo-gov-number" v-model="tenant.trustNumber" required />
        </div>
        <div class="form-group">
          <label for="vat-number">VAT Number:</label>
          <input type="text" id="vat-number" v-model="tenant.vatNumber" required />
        </div>
      </div>
      <!-- Address Information -->
      <div class="section">
        <h3>Address Information</h3>
        <div class="form-group">
          <label for="street">Street:</label>
          <input type="text" id="street" v-model="tenant.address.street" required />
        </div>
        <div class="form-group">
          <label for="city">City:</label>
          <input type="text" id="city" v-model="tenant.address.city" required />
        </div>
        <div class="form-group">
          <label for="state">State/Province:</label>
          <input type="text" id="state" v-model="tenant.address.state" required />
        </div>
        <div class="form-group">
          <label for="postal-code">Postal Code:</label>
          <input type="text" id="postal-code" v-model="tenant.address.postalCode" required />
        </div>
        <div class="form-group">
          <label for="country">Country:</label>
          <input type="text" id="country" v-model="tenant.address.country" required />
        </div>
      </div>
      <!-- Bank Account Details -->
      <div class="section">
        <h3>Bank Account Details</h3>
        <div class="form-group">
          <label for="account-name">Account Name:</label>
          <input type="text" id="account-name" v-model="tenant.bankAccount.name" />
        </div>
        <div class="form-group">
          <label for="account-number">Account Number:</label>
          <input type="text" id="account-number" v-model="tenant.bankAccount.number" />
        </div>
        <div class="form-group">
          <label for="selected-bank">Bank Name:</label>
          <input type="text" id="selected-bank" v-model="tenant.bankAccount.bank" />
        </div>
        <div class="form-group">
          <label for="account-type">Account Type:</label>
          <input type="text" id="account-type" v-model="tenant.bankAccount.type" />
        </div>
        <div class="form-group">
          <label for="branch-name">Branch Name:</label>
          <input type="text" id="branch-name" v-model="tenant.bankAccount.branchName" />
        </div>
        <div class="form-group">
          <label for="branch-code">Branch Code:</label>
          <input type="text" id="branch-code" v-model="tenant.bankAccount.branchCode" />
        </div>
      </div>
      <!-- Submit Button -->
      <button type="submit" :disabled="loading">Add Tenant</button>
    </form>
  </div>
</template>

<script>
import { addDoc, collection, getFirestore, query, orderBy, limit, getDocs } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import * as XLSX from 'xlsx';

const db = getFirestore();
const auth = getAuth();

export default {
  data() {
    return {
      tenant: {
        entityId: "",
        reference: "",
        type: "Residential",
        legalName: "",
        placed: false,
        tradingName: "",
        registrationNumber: "",
        trustNumber: "",
        vatNumber: "",
        email: "",
        number: "",
        address: {
          street: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
        },
        person: {
          firstName: "",
          surname: "",
          dateOfBirth: "",
        },
        bankAccount: {
          name: "",
          number: "",
          bank: "",
          type: "",
          branchName: "",
          branchCode: "",
        },
        displayAs: "",
        userUid: "",
      },
      loading: false,
      showImportModal: false,
      selectedFile: null,
      selectedFileName: "",
      previewData: [],
      importLoading: false
    };
  },
  methods: {
    async submitTenant() {
      if (this.loading) return;
      this.loading = true;
      try {
        const user = auth.currentUser;
        if (!user) {
          alert("You must be logged in to add a tenant.");
          this.loading = false;
          return;
        }
        this.tenant.userUid = user.uid;
        const tenantRefQuery = query(collection(db, "tenants"), orderBy("reference", "desc"), limit(1));
        const querySnapshot = await getDocs(tenantRefQuery);
        let newRef = "TEN000001";
        if (!querySnapshot.empty) {
          const lastTenant = querySnapshot.docs[0].data();
          const lastRef = lastTenant.reference;
          const lastNum = parseInt(lastRef.replace("TEN", ""));
          newRef = "TEN" + (lastNum + 1).toString().padStart(6, "0");
        }
        this.tenant.reference = newRef;
        const tenantData = { ...this.tenant };
        await addDoc(collection(db, "tenants"), tenantData);
        alert("Tenant added successfully!");
        this.$router.push({ path: "/tenants", query: { type: "tenants" } });
        this.$router.go();
        this.loading = false;
      } catch (error) {
        console.error("Error adding tenant:", error);
        this.loading = false;
        alert("Error adding tenant. Please try again later.");
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.selectedFile = file;
      this.selectedFileName = file.name;
      this.previewData = [];

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Find header row (skip empty rows at the beginning)
          let headerRowIndex = 0;
          for (let i = 0; i < jsonData.length; i++) {
            if (jsonData[i] && jsonData[i].length > 0 && 
                jsonData[i].some(cell => cell && typeof cell === 'string' && 
                (cell.includes('REFERENCE') || cell.includes('FIRST') || cell.includes('SURNAME')))) {
              headerRowIndex = i;
              break;
            }
          }

          const headers = jsonData[headerRowIndex];
          const dataRows = jsonData.slice(headerRowIndex + 1);

          // Map column indices
          const refIndex = headers.findIndex(h => h && h.toString().includes('REFERENCE'));
          const firstNameIndex = headers.findIndex(h => h && h.toString().includes('FIRST'));
          const surnameIndex = headers.findIndex(h => h && h.toString().includes('SURNAME'));
          const emailIndex = headers.findIndex(h => h && h.toString().includes('EMAIL'));
          const phoneIndex = headers.findIndex(h => h && h.toString().includes('NUMBERS'));
          const legalNameIndex = headers.findIndex(h => h && h.toString().includes('LEGAL NAME'));
          const tradingNameIndex = headers.findIndex(h => h && h.toString().includes('TRADING NAME'));

          // Process first 5 rows for preview
          const previewRows = dataRows.slice(0, 5).filter(row => row && row.length > 0);
          
          this.previewData = previewRows.map(row => {
            const firstName = row[firstNameIndex] || '';
            const surname = row[surnameIndex] || '';
            const displayAs = `${firstName} ${surname}`.trim() || row[legalNameIndex] || row[tradingNameIndex] || 'Unknown';

            return {
              reference: row[refIndex] || 'N/A',
              displayAs: displayAs,
              person: {
                firstName: firstName,
                surname: surname
              },
              email: row[emailIndex] || 'N/A',
              number: row[phoneIndex] || 'N/A'
            };
          });

        } catch (error) {
          console.error('Error reading Excel file:', error);
          alert('Error reading Excel file. Please make sure it\'s a valid Excel file.');
        }
      };
      reader.readAsArrayBuffer(file);
    },

    async importTenants() {
      if (!this.selectedFile) {
        alert('Please select a file first.');
        return;
      }

      this.importLoading = true;
      try {
        const user = auth.currentUser;
        if (!user) {
          alert("You must be logged in to import tenants.");
          this.importLoading = false;
          return;
        }

        const data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(new Uint8Array(e.target.result));
          reader.onerror = reject;
          reader.readAsArrayBuffer(this.selectedFile);
        });

        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Find header row
        let headerRowIndex = 0;
        for (let i = 0; i < jsonData.length; i++) {
          if (jsonData[i] && jsonData[i].length > 0 && 
              jsonData[i].some(cell => cell && typeof cell === 'string' && 
              (cell.includes('REFERENCE') || cell.includes('FIRST') || cell.includes('SURNAME')))) {
            headerRowIndex = i;
            break;
          }
        }

        const headers = jsonData[headerRowIndex];
        const dataRows = jsonData.slice(headerRowIndex + 1).filter(row => row && row.length > 0);

        // Map column indices
        const refIndex = headers.findIndex(h => h && h.toString().includes('REFERENCE'));
        const firstNameIndex = headers.findIndex(h => h && h.toString().includes('FIRST'));
        const surnameIndex = headers.findIndex(h => h && h.toString().includes('SURNAME'));
        const emailIndex = headers.findIndex(h => h && h.toString().includes('EMAIL'));
        const phoneIndex = headers.findIndex(h => h && h.toString().includes('NUMBERS'));
        const legalNameIndex = headers.findIndex(h => h && h.toString().includes('LEGAL NAME'));
        const tradingNameIndex = headers.findIndex(h => h && h.toString().includes('TRADING NAME'));
        const identifierIndex = headers.findIndex(h => h && h.toString().includes('IDENTIFIER'));
        const dobIndex = headers.findIndex(h => h && h.toString().includes('DATE OF BIRTH'));
        const vatIndex = headers.findIndex(h => h && h.toString().includes('VAT NUMBER'));
        const typeIndex = headers.findIndex(h => h && h.toString().includes('TYPE'));
        const stateIndex = headers.findIndex(h => h && h.toString().includes('STATE'));

        // Get last reference for sequential numbering
        const tenantRefQuery = query(collection(db, "tenants"), orderBy("reference", "desc"), limit(1));
        const querySnapshot = await getDocs(tenantRefQuery);
        let lastRefNum = 0;
        if (!querySnapshot.empty) {
          const lastTenant = querySnapshot.docs[0].data();
          const lastRef = lastTenant.reference;
          lastRefNum = parseInt(lastRef.replace("TEN", "")) || 0;
        }

        let successCount = 0;
        let errorCount = 0;

        for (let i = 0; i < dataRows.length; i++) {
          try {
            const row = dataRows[i];
            if (!row || row.length === 0) continue;

            const firstName = row[firstNameIndex] || '';
            const surname = row[surnameIndex] || '';
            const legalName = row[legalNameIndex] || '';
            const tradingName = row[tradingNameIndex] || '';

            // Determine if it's commercial or residential
            const isCommercial = legalName || tradingName;
            const displayAs = `${firstName} ${surname}`.trim() || legalName || tradingName || 'Unknown';

            // Generate new reference
            lastRefNum++;
            const newRef = "TEN" + lastRefNum.toString().padStart(6, "0");

            const tenantData = {
              reference: newRef,
              type: isCommercial ? "Commercial" : "Residential",
              legalName: legalName,
              tradingName: tradingName,
              registrationNumber: row[identifierIndex] || '',
              vatNumber: row[vatIndex] || '',
              email: row[emailIndex] || '',
              number: row[phoneIndex] || '',
              identifier: row[identifierIndex] || '',
              address: {
                street: "",
                city: "",
                state: row[stateIndex] || "",
                postalCode: "",
                country: "",
              },
              person: {
                firstName: firstName,
                surname: surname,
                dateOfBirth: this.formatDate(row[dobIndex]) || "",
              },
              bankAccount: {
                name: "",
                number: "",
                bank: "",
                type: "",
                branchName: "",
                branchCode: "",
              },
              displayAs: displayAs,
              userUid: user.uid,
              placed: false,
              imported: true
            };

            await addDoc(collection(db, "tenants"), tenantData);
            successCount++;

            // Small delay to avoid overwhelming Firestore
            await new Promise(resolve => setTimeout(resolve, 100));

          } catch (error) {
            console.error(`Error importing row ${i + 1}:`, error);
            errorCount++;
          }
        }

        alert(`Import completed!\nSuccess: ${successCount}\nErrors: ${errorCount}`);
        this.showImportModal = false;
        this.selectedFile = null;
        this.selectedFileName = "";
        this.previewData = [];

        // Refresh the tenants list
        this.$router.push({ path: "/tenants", query: { type: "tenants", refresh: true } });

      } catch (error) {
        console.error('Error during import:', error);
        alert('Error importing tenants. Please try again.');
      } finally {
        this.importLoading = false;
      }
    },

    formatDate(dateValue) {
      if (!dateValue) return '';
      
      // Handle Excel serial date numbers
      if (typeof dateValue === 'number') {
        const excelEpoch = new Date(1899, 11, 30);
        const milliseconds = (dateValue - 1) * 24 * 60 * 60 * 1000;
        const date = new Date(excelEpoch.getTime() + milliseconds);
        return date.toISOString().split('T')[0];
      }
      
      // Handle string dates
      if (typeof dateValue === 'string') {
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      }
      
      return '';
    }
  }
};
</script>

<style scoped>
.tenant-form {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.import-btn {
  background-color: #2196F3;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.import-btn:hover {
  background-color: #1976D2;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ddd;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.modal-body {
  padding: 20px;
}

.file-upload-section {
  margin-bottom: 20px;
}

.file-upload-section input[type="file"] {
  display: none;
}

.file-label {
  display: inline-block;
  padding: 10px 20px;
  background-color: #f5f5f5;
  border: 2px dashed #ddd;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  width: 100%;
}

.file-label:hover {
  background-color: #e9e9e9;
}

.preview-section {
  margin: 20px 0;
}

.preview-section h4 {
  margin-bottom: 10px;
}

.table-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
}

.preview-table th,
.preview-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #ddd;
  text-align: left;
  font-size: 12px;
}

.preview-table th {
  background-color: #f5f5f5;
  position: sticky;
  top: 0;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.btn-primary {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

/* Existing form styles */
h2, h3 {
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

input, select {
  width: 100%;
  padding: 8px;
  margin-top: 5px;
  border: 2px solid #d5d3d3;
  border-radius: 4px;
  outline: none;
}

input:focus, select:focus {
  border-color: #4caf50;
}

button {
  padding: 10px 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  cursor: pointer;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.section {
  margin-bottom: 30px;
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 5px;
}

.section h3 {
  margin-top: 0;
}
</style>